/* eslint-disable */
import React, { useEffect } from 'react';
import { Button, Checkbox, Drawer, Input, Modal, Typography } from 'antd';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';
import { useGetApplication } from '~/controllers/wizard';
import { ErrorModal } from '~/components';
import ContentHeader from '~/components/ContentHeader';
import { sendCompanyValidate } from '~/api/validate';
import paths from '~/pages/paths';
import theme from '~/constants/theme';
import { ConfirmButton } from '~/layouts/ksa/CompanyInfoLayout';
import Form from './components/Form';
import { getpersonalIdentificationType } from '~/utils/textOnlyValidation';
import { useIsMobile, useIsTablet } from '~/hooks/useScreenSizes';
import { WarningIcon } from '~/img';
import '../../../img/CRHint.png';
import { useDirection } from '~/hooks/i18nHooks';
import MobileFormItem from '~/components/MobileFormItem';
import { getTrackingId, handleGetutm } from '~/utils/handleGetUtm';
import { getIdentityValidationFieldError, isValidationServerError } from './identityValidationErrors';

const DataValidation: React.FC = () => {
  const history = useHistory();
  const dir = useDirection();
  const [isLoading, setIsLoading] = React.useState(false);
  const [openPreviewModal, setOpenPreviewModal] = React.useState(false);
  const [showError, setShowError] = React.useState<{ visible: boolean; error: any }>({
    visible: false,
    error: null,
  });
  const { t } = useTranslation('ksaApplication');
  const { t: tc } = useTranslation('common');
  const application = useGetApplication();

  const [form] = Form.useForm();

  // Prefill from the previously submitted/persisted identity data so that
  // returning to this step (e.g. from Business Details) doesn't show a blank form.
  useEffect(() => {
    const payload: any = application.data?.payload;
    if (!payload) return;

    const crNumberOrEntityNumber = payload?.thiqahFullInfo?.crNationalNumber;
    const personalIdentificationNumber =
      payload?.applicantDetails?.nationalId ?? payload?.elmInfo?.id;
    const personalPhoneNumber =
      payload?.applicantDetails?.phoneNumber ?? payload?.elmInfo?.mobile;

    // The mobile input stores/expects the value as "+966" + 9 local digits.
    // Normalize whatever format was persisted so it renders and validates.
    const normalizePhone = (raw?: string) => {
      if (!raw) return undefined;
      const digits = String(raw).replace(/\D/g, '');
      if (!digits) return undefined;
      // Strip leading country code (966) before extracting local digits,
      // otherwise slice(-9) bites into the country code digits.
      const local = digits.startsWith('966') ? digits.slice(3) : digits;
      return `+966${local.slice(-9)}`;
    };
    const normalizedPhone = normalizePhone(personalPhoneNumber);

    const current = form.getFieldsValue();
    const next: Record<string, any> = {};
    if (crNumberOrEntityNumber && !current.crNumberOrEntityNumber) {
      next.crNumberOrEntityNumber = crNumberOrEntityNumber;
    }
    if (personalIdentificationNumber && !current.personalIdentificationNumber) {
      next.personalIdentificationNumber = personalIdentificationNumber;
    }
    if (normalizedPhone && !current.personalPhoneNumber) {
      next.personalPhoneNumber = normalizedPhone;
    }
    if (Object.keys(next).length) {
      form.setFieldsValue(next);
    }
  }, [application.data?.payload, form]);

  const validateCRNumberOrEntityNumber = (value: any) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^\d+$/.test(value) || value.length !== 10 || value[0] !== '7') {
      return Promise.reject(t('Please enter a valid 10-digit Unified National Number starting with (7).'));
    }
    return Promise.resolve();
  };
  const validateIdentificationNumber = (value: any) => {
    if (!value) {
      return Promise.resolve();
    }

    if (!/^\d+$/.test(value) || value.length !== 10 || (value[0] != 1 && value[0] != 2)) {
      return Promise.reject(t('Please enter 10 digits valid ID/Iqama number.'));
    }
    return Promise.resolve();
  };
  const validatePersonalPhoneNumber = (value: any) => {
    if (!value || value === '+966') {
      return Promise.resolve(); // handled by required rule
    }

    // value stored as '+966' + local digits; strip prefix to inspect local part
    const localDigits = value.startsWith('+966') ? value.slice(4) : value;
    if (localDigits.length === 0) {
      return Promise.resolve(); // handled by required rule
    }

    if (!/^\d+$/.test(localDigits) || localDigits.length < 8) {
      return Promise.reject(t('Please enter a valid mobile number.'));
    }
    return Promise.resolve();
  };

  // Helper function to track form submission analytics
  const trackFormSubmit = () => {
    (window as any).dataLayer.push({
      event: 'event_formsubmit',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      page_section: 'Initial Information',
      user_id: localStorage.getItem('sob_temp_uid'),
      trackingId: getTrackingId(application?.data),
    });
  };

  // Helper function to track field errors
  const trackFieldError = (errorDescription: string) => {
    (window as any).dataLayer.push({
      event: 'event_fielderror',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      form_fieldname: 'personalIdentificationNumber',
      form_fielderror: errorDescription,
      user_id: localStorage.getItem('sob_temp_uid'),
    });
  };

  // Helper function to navigate to OTP page
  const navigateToOTP = (smsOtpChallengeId: string, maskedPhone: string, formData: any) => {
    const utmParams = handleGetutm(application?.data);
    history.push(
      `${paths.ksa.dataValidation.smsotp}?${utmParams}`
        .replace(':smsOtpChallengeId', smsOtpChallengeId)
        .replace(':mobileNumber', maskedPhone),
      formData
    );
  };

  const onSubmit = async ({
    crNumberOrEntityNumber,
    personalIdentificationNumber,
    personalPhoneNumber,
  }: {
    crNumberOrEntityNumber: string;
    personalIdentificationNumber: string;
    personalPhoneNumber: string;
  }) => {
    if (isLoading) {
      console.log('Form submission blocked - already loading');
      return;
    }
    const finalCRNumber = crNumberOrEntityNumber;
    const personalIdentificationType = getpersonalIdentificationType(personalIdentificationNumber);

    setIsLoading(true);

    try {
      const res = await sendCompanyValidate({
        crNumberOrEntityNumber: finalCRNumber,
        personalIdentificationType,
        personalIdentificationNumber,
        personalPhoneNumber,
      });

      const maskedPhone = personalPhoneNumber.replace(/.(?=.{3})/g, '*');
      navigateToOTP(res.smsOtpChallengeId, maskedPhone, {
        crNumberOrEntityNumber: finalCRNumber,
        personalIdentificationType,
        personalIdentificationNumber,
        personalPhoneNumber,
      });
      trackFormSubmit();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      const errorData = error?.response?.data;
      const serverError = isValidationServerError(
        error?.response?.status,
        errorData?.description || errorData?.message
      );
      const errorToShow = serverError
        ? { description: t('Unable to validate your details right now. Please try again later.') }
        : errorData;
      trackFieldError(errorToShow?.description);

      if (serverError) {
        setShowError({ visible: true, error: errorToShow });
        return;
      }

      const code = error?.response?.data?.code || errorToShow?.code;
      const description = errorToShow?.description || errorToShow?.message;
      let fieldName = '';
      let errorMessage = '';
      const identityFieldError = getIdentityValidationFieldError(code);

      if (identityFieldError) {
        fieldName = identityFieldError.fieldName;
        errorMessage = t(identityFieldError.messageKey);
      } else if (
        [
          'MOBILE_VERIFICATION_INVALID_NUMBER',
          'MOBILE_VERIFICATION_INVALID_ID',
        ].includes(code) ||
        (description && description.toLowerCase().includes('number not registered')) ||
        (description && description.toLowerCase().includes('mobile')) ||
        (description && description.toLowerCase().includes('phone'))
      ) {
        fieldName = 'personalPhoneNumber';
        errorMessage = t(
          code === 'MOBILE_VERIFICATION_INVALID_NUMBER' || code === 'MOBILE_VERIFICATION_INVALID_ID'
            ? code
            : 'MOBILE_VERIFICATION_NOT_OWNER'
        );
      } else if (
        [
          'OWNER_NOT_FOUND',
          'OWNER_ID_NOT_FOUND',
          'INVALID_OWNER_ID',
          'NATIONAL_ID_NOT_FOUND',
        ].includes(code) ||
        (description && description.toLowerCase().includes('owner')) ||
        (description && description.toLowerCase().includes('id not found')) ||
        (description && description.toLowerCase().includes('national id')) ||
        (description && description.toLowerCase().includes('iqama'))
      ) {
        fieldName = 'personalIdentificationNumber';
        errorMessage = t('OWNER_NOT_FOUND') !== 'OWNER_NOT_FOUND' ? t('OWNER_NOT_FOUND') : 'ID not found under the company structure.';
      } else if (
        code === 'COMPANY_NOT_ACTIVE' ||
        (description && description.toLowerCase().includes('cr number')) ||
        (description && description.toLowerCase().includes('unified'))
      ) {
        fieldName = 'crNumberOrEntityNumber';
        errorMessage = t('COMPANY_NOT_ACTIVE') !== 'COMPANY_NOT_ACTIVE' ? t('COMPANY_NOT_ACTIVE') : 'CR Number not found in official records.';
      }

      if (fieldName && errorMessage) {
        form.setFields([
          {
            name: fieldName,
            errors: [errorMessage],
          },
        ]);
      } else {
        setShowError({ visible: true, error: errorToShow });
      }
    }
  };
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  return (
    <>
      <Form
        form={form}
        style={{ backgroundColor: isTablet ? 'transparent' : undefined }}
        name="dataValidationForm"
        onFinish={onSubmit}
        validateTrigger={['onSubmit', 'onBlur']}
        preserve={true}
      >
        <ContentHeader
          title={t('Initial Information')}
          subtitle={t(
            'To activate your account, we’ll need to verify your business details. This takes just a few minutes.'
          )}
          stageNumber={2}
        />
        <Container dir={dir} isMobile={isTablet}>

          {/* National ID/Iqama */}
          <Typography.Paragraph className="labelText">
            {t('National ID/Iqama')}
          </Typography.Paragraph>
          <FormInput style={{ height: 'auto' }}>
            <FormItem
              name="personalIdentificationNumber"
              rules={[
                { required: true, message: t('Please enter 10 digits valid ID/Iqama number.') },
                {
                  validator: (_: any, value: any) => validateIdentificationNumber(value),
                },
              ]}
            >
              <InputValue name="PersonalIdentificationNumber" disabled={isLoading} />
            </FormItem>
          </FormInput>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const hasError = form.getFieldError('personalIdentificationNumber')?.length > 0;
              if (hasError) return null;
              return (
                <Typography.Paragraph className="hintText">
                  {dir === 'rtl' ? (
                    <>
                      {t('National ID or Iqama number')} <strong>{t('10-digit')}</strong>
                    </>
                  ) : (
                    <>
                      <strong>{t('10-digit')}</strong> {t('National ID or Iqama number')}
                    </>
                  )}
                </Typography.Paragraph>
              );
            }}
          </Form.Item>

          {/* Mobile Number */}
          <Typography.Paragraph className="labelText">
            {t('Mobile Number')}
          </Typography.Paragraph>
          <FormInput dir="ltr">
            <MobileFormItem
              name={['personalPhoneNumber']}
              required
              rules={[
                { required: true, message: t('Please enter a valid mobile number.') },
                {
                  validator: (_: any, value: any) => validatePersonalPhoneNumber(value),
                },
              ]}
            />
          </FormInput>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const hasError =
                form.getFieldError('personalPhoneNumber')?.length > 0 ||
                form.getFieldError(['personalPhoneNumber'])?.length > 0;
              if (hasError) return null;
              return (
                <InfoBox>
                  {t('Please use a mobile number registered with your National ID/Iqama for faster verification.')}
                </InfoBox>
              );
            }}
          </Form.Item>

          {/* Unified National Number */}
          <Typography.Paragraph className="labelText">
            {t('Unified National Number')}
            <WarningIcon
              onClick={() => {
                setOpenPreviewModal(true);
              }}
              style={{
                border: '1px solid black',
                borderRadius: 50,
                width: 15,
                height: 15,
                marginLeft: 10,
                marginRight: 10,
                paddingTop: 2,
                fontSize: 9,
              }}
              className="warningIcon"
            />
          </Typography.Paragraph>
          <FormInput style={{ height: 'auto' }}>
            <FormItem
              name="crNumberOrEntityNumber"
              rules={[
                {
                  validateFirst: true,
                  required: true,
                  message: t('Please enter a valid 10-digit Unified National Number starting with (7).'),
                },
                {
                  validator: (_: any, value: any) => validateCRNumberOrEntityNumber(value),
                },
              ]}
            >
              <InputValue name="crnumber" disabled={isLoading} />
            </FormItem>
          </FormInput>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const hasError = form.getFieldError('crNumberOrEntityNumber')?.length > 0;
              if (hasError) return null;
              return (
                <Typography.Paragraph className="hintText">
                  {t('Please enter a valid 10-digit Unified National Number starting with (7).')}
                </Typography.Paragraph>
              );
            }}
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const nationalId = form.getFieldValue('personalIdentificationNumber');
              const mobileNumber = form.getFieldValue('personalPhoneNumber');
              const crNumber = form.getFieldValue('crNumberOrEntityNumber');
              const hasAnyValue =
                (nationalId && String(nationalId).trim() !== '') ||
                (mobileNumber && String(mobileNumber).trim() !== '') ||
                (crNumber && String(crNumber).trim() !== '');
              return (
                <StyledConfirmButton
                  id="dataValidationForm_submit"
                  type="primary"
                  htmlType="submit"
                  dir={dir}
                  loading={isLoading}
                  disabled={!hasAnyValue}
                  style={{ marginTop: 32 }}
                >
                  {tc('Continue')}
                </StyledConfirmButton>
              );
            }}
          </Form.Item>
          {isMobile && (
            <Button
              type="text"
              style={{ width: '100%', marginTop: 20, fontWeight: 600, color: '#0069B1' }}
              onClick={() => {
                history.push(paths.ksa.contactUs);
              }}
            >
              {t('Contact Us')}
            </Button>
          )}
          <ErrorModal
            visible={showError?.visible}
            error={showError?.error}
            onClose={() => setShowError({ visible: false, error: null })}
          />
        </Container>
      </Form>
      <HintDrawerStyle />
      {isMobile ? (
        <Drawer
          className="hint-preview-drawer"
          placement="bottom"
          open={openPreviewModal}
          onClose={() => setOpenPreviewModal(false)}
          title={t('Unified National Number')}
          height="auto"
          closable={false}
          extra={
            <button
              onClick={() => setOpenPreviewModal(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M7 21L21 7M7 7L21 21" stroke="#070707" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          }
          bodyStyle={{ padding: '12px 16px 74px' }}
          headerStyle={{
            borderBottom: '1px solid #f0f0f0',
            padding: '16px 16px',
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          <img
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: 8,
            }}
            src="../../../img/CRHint.png"
            alt="HintIcon"
          />
        </Drawer>
      ) : (
        <Modal
          centered
          open={openPreviewModal}
          onCancel={() => setOpenPreviewModal(false)}
          footer={null}
          width={900}
        >
          <img
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: 8,
            }}
            src="../../../img/CRHint.png"
            alt="HintIcon"
          />
        </Modal>
      )}
    </>
  );
};

const HintDrawerStyle = createGlobalStyle`
  .hint-preview-drawer .ant-drawer-content {
    border-radius: 20px 20px 0 0;
    background: #ffffff;
  }
`;

const Container = styled.div<any>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  max-width: 570px;
  min-width: 0;
  box-sizing: border-box;

  .labelText {
    color: #070707;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 4px;
  }
  .hintText {
    color: #808080;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    margin-top: 4px;
    margin-bottom: 24px;
  }
  .labelHelpText {
    color: #808080;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }
`;

const InfoBox = styled.div`
  border-radius: 8px;
  background: #E6F0F7;
  box-shadow: 0 2px 6px 0 rgba(0, 89, 150, 0.03), 0 -1px 8px -10px rgba(0, 89, 150, 0.03);
  margin-bottom: 24px;
  color: #020202ff;
  font-size: 14px;
  font-family: Inter, sans-serif;
  line-height: 20px;
  padding: 12px 16px;
  width: 100%;
  max-width: 570px;
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.sm}px) {
    display: flex;
    padding: 16px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    align-self: stretch;
  }
`;

const InputValue = styled(Input as any)`
  direction: ltr !important;
  text-align: left;
  width: 100% !important;
  max-width: 570px !important;
  min-width: 0;
  height: 56px !important;

  .ant-input-group-addon {
    direction: ltr !important;
  }

  input {
    direction: ltr !important;
    text-align: left;
    height: 56px !important;
  }
`;

const FormInput = styled.div`
  height: auto;
  width: 100%;
  max-width: 570px;
  min-width: 0;
  box-sizing: border-box;
  margin-bottom: 8px;

  .ant-form-item {
    margin-bottom: 0px !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  .ant-input-affix-wrapper,
  .ant-input {
    width: 100% !important;
    min-width: 0 !important;
  }

  .phoneInput-country {
    height: 56px !important;
  }
`;

const FormItem = styled(Form.Item as any)`
  @media (max-width: ${theme.breakpoints.md}px) {
    max-width: 100% !important;
  }
`;

const StyledConfirmButton = styled(ConfirmButton)`
  // height: 60px !important;
  font-size: 16px !important;
  width: 100% !important;
  max-width: 570px !important;
  min-width: 0 !important;
  box-sizing: border-box;
`;

const CheckboxItem = styled(Checkbox as any)`
  .ant-checkbox-inner {
    border-radius: 2px !important;
  }
`;

const SubTitle = styled.div`
  margin-bottom: 32px;
`;

const CheckboxFormItems = styled(Form.Item as any)`
  max-width: unset !important;
`;

export default DataValidation;
